import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  type DragMoveEvent,
  type DragOverEvent,
  type DragStartEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  horizontalListSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { flexRender, type Header } from '@tanstack/react-table';
import { ArrowDown, ArrowUp, ArrowUpDown, GripHorizontal } from 'lucide-react';
import { type CSSProperties, useId, useLayoutEffect, useState } from 'react';

import type {
  DataTableFilterOption,
  ColumnHeaderProps,
  DataTableHeaderProps,
} from '~/components/common/data-table/data-table.types';
import { Input } from '~/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/components/ui/select';
import { TableHead, TableHeader, TableRow } from '~/components/ui/table';
import { cn } from '~/utils/cn';

import { isFirstRightPinned, isLastLeftPinned, pinnedCellStyle } from './data-table-pinning';

/** How much one arrow-key press moves a column edge. Coarse enough to be useful over
 *  a few presses, fine enough to land on a specific width. */
const KEYBOARD_RESIZE_STEP = 16;

/** A column is draggable when reordering is on and it is neither pinned (pinned
 *  columns stay put) nor an injected control column (`__select__` / `__expand__`). */
function isReorderable<T>(header: Header<T, unknown>, enableColumnReordering: boolean): boolean {
  return (
    enableColumnReordering && !header.column.getIsPinned() && !header.column.id.startsWith('__')
  );
}

/**
 * Renders the table header with optional sort buttons, column filter inputs,
 * drag-to-resize dividers, drag-to-reorder handles, and sticky pinned columns.
 *
 * - Sorting: clicking a header cycles through asc → desc → unsorted.
 * - Filtering: a text input below the label narrows rows for that column.
 * - Resizing: a divider on each column's trailing edge, draggable or arrow-keyable.
 * - Reordering: a grip handle drags a column to a new position (dnd-kit).
 * - Pinning: a left-pinned column stays visible as the table scrolls sideways.
 */
export function DataTableHeader<T>({
  table,
  enableSorting,
  enableFiltering,
  enableColumnResizing,
  enableColumnReordering,
  onReorder,
  testId = 'data-table',
}: DataTableHeaderProps<T>) {
  const sensors = useSensors(
    // A small activation distance keeps a plain click (sort) from starting a drag.
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  // The column being dragged, where it would land, and how far it has moved — painted onto the
  // WHOLE column (header + every body cell, which carry `data-column-id`) by the scoped <style>
  // below, so the column visibly travels with the pointer, not just its header.
  const scope = useId();
  const [drag, setDrag] = useState<{ active: string; over: string | null; dx: number } | null>(
    null,
  );

  // A <style> may not live inside <thead>: the drag rules go to one element in <head>, updated as
  // the pointer moves and removed when the drag ends.
  useLayoutEffect(() => {
    if (!drag) return;
    const el = document.createElement('style');
    el.textContent = dragColumnStyle(scope, drag);
    document.head.appendChild(el);
    return () => {
      el.remove();
    };
  }, [drag, scope]);

  function handleDragStart(event: DragStartEvent): void {
    setDrag({ active: String(event.active.id), over: null, dx: 0 });
  }
  function handleDragMove(event: DragMoveEvent): void {
    setDrag((prev) => (prev ? { ...prev, dx: event.delta.x } : prev));
  }
  function handleDragOver(event: DragOverEvent): void {
    const over = event.over ? String(event.over.id) : null;
    setDrag((prev) => (prev ? { ...prev, over } : prev));
  }
  function handleDragEnd(event: DragEndEvent): void {
    const { active, over } = event;
    setDrag(null);
    if (over && active.id !== over.id) onReorder(String(active.id), String(over.id));
  }

  const renderHeader = (header: Header<T, unknown>) =>
    isReorderable(header, enableColumnReordering) ? (
      <SortableColumnHeader
        key={header.id}
        header={header}
        enableSorting={enableSorting}
        enableFiltering={enableFiltering}
        enableColumnResizing={enableColumnResizing}
        reorderable
        testId={testId}
      />
    ) : (
      <ColumnHeader
        key={header.id}
        header={header}
        enableSorting={enableSorting}
        enableFiltering={enableFiltering}
        enableColumnResizing={enableColumnResizing}
        reorderable={false}
        testId={testId}
      />
    );

  const headerRows = table.getHeaderGroups().map((headerGroup) => (
    <TableRow key={headerGroup.id}>
      {/* The select-all checkbox is rendered by the injected `__select__`
          column's own header (see DataTable), so it is not duplicated here. */}
      {headerGroup.headers
        .filter((header) => header.column.getIsPinned() !== 'right')
        .map(renderHeader)}
      {/* Surplus absorber, resizing only. Under `table-fixed` a column with no
          specified width takes whatever space is left over, so when the lane is
          wider than the column total this cell soaks up the difference and every
          real column still renders at exactly `header.getSize()`. It sits BEFORE any
          right-pinned column, so a pinned actions column stays the last one — never an
          empty column after it. */}
      {enableColumnResizing && <TableHead aria-hidden="true" className="w-auto p-0" />}
      {headerGroup.headers
        .filter((header) => header.column.getIsPinned() === 'right')
        .map(renderHeader)}
    </TableRow>
  ));

  return (
    // z-30, above the frozen body cells (z-10): at the same layer, frozen cells
    // scrolled OVER the sticky header when the table scrolled vertically.
    // Square corners: the scroll container already clips to its rounded border, and a rounded
    // corner here left a curved notch beside the scrollbar.
    <TableHeader
      data-dnd-scope={scope}
      className="bg-neutral-30 sticky top-0 z-30"
      data-testid={`${testId}-header`}
    >
      {enableColumnReordering ? (
        // dnd-kit's screen-reader text/live region are <div>s — portal them to <body>, since a
        // <div> inside <thead> is invalid DOM nesting.
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragStart={handleDragStart}
          onDragMove={handleDragMove}
          onDragOver={handleDragOver}
          onDragEnd={handleDragEnd}
          onDragCancel={() => {
            setDrag(null);
          }}
          accessibility={{ container: document.body }}
        >
          <SortableContext
            items={table
              .getVisibleLeafColumns()
              .filter((c) => !c.getIsPinned() && !c.id.startsWith('__'))
              .map((c) => c.id)}
            strategy={horizontalListSortingStrategy}
          >
            {headerRows}
          </SortableContext>
        </DndContext>
      ) : (
        headerRows
      )}
    </TableHeader>
  );
}

/** The label + sort button + filter input shared by both the plain and the
 *  drag-sortable header cells. */
function ColumnHeaderInner<T>({
  header,
  enableSorting,
  enableFiltering,
  testId,
}: Pick<ColumnHeaderProps<T>, 'header' | 'enableSorting' | 'enableFiltering' | 'testId'>) {
  const canSort = enableSorting && header.column.getCanSort();
  const canFilter = enableFiltering && header.column.getCanFilter();
  const sorted = header.column.getIsSorted();
  const columnId = header.column.id;

  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      {canSort ? (
        <button
          type="button"
          className="group/sort text-foreground flex w-full min-w-0 cursor-pointer items-center justify-between gap-1.5 text-left text-sm font-medium select-none"
          onClick={header.column.getToggleSortingHandler() ?? undefined}
          aria-label={`Sort by ${columnId}`}
          data-testid={`${testId}-sort-${columnId}`}
        >
          <span className="min-w-0 truncate">
            {flexRender(header.column.columnDef.header, header.getContext())}
          </span>
          <SortIcon sorted={sorted} />
        </button>
      ) : (
        <span className="truncate">
          {flexRender(header.column.columnDef.header, header.getContext())}
        </span>
      )}

      {canFilter && header.column.columnDef.meta?.filterOptions ? (
        <HeaderFilterSelect
          columnId={columnId}
          options={header.column.columnDef.meta.filterOptions}
          value={(header.column.getFilterValue() as string | undefined) ?? ''}
          onChange={(next) => {
            header.column.setFilterValue(next);
          }}
          testId={testId}
        />
      ) : (
        canFilter && (
          <Input
            placeholder="Filter..."
            data-testid={`${testId}-filter-${columnId}`}
            value={(header.column.getFilterValue() as string | undefined) ?? ''}
            onChange={(e) => {
              header.column.setFilterValue(e.target.value);
            }}
            className="h-7 text-xs"
            aria-label={`Filter ${columnId}`}
          />
        )
      )}
    </div>
  );
}

/** Classes + inline style shared by every header cell — the pinned-column sticky
 *  offset, the divider on the last pinned column, and the resize width. */
function headerCellChrome<T>(
  header: Header<T, unknown>,
  enableColumnResizing: boolean,
): { className: string | undefined; style: CSSProperties | undefined } {
  const isPinned = Boolean(header.column.getIsPinned());
  const className = cn(
    header.column.columnDef.meta?.className,
    header.column.columnDef.meta?.headerClassName,
    // Opaque so scrolling columns don't bleed through the frozen one.
    isPinned && 'bg-neutral-30',
    isLastLeftPinned(header.column) && 'border-border border-r',
    isFirstRightPinned(header.column) &&
      'shadow-[inset_1px_0_0_var(--border),-6px_0_8px_-6px_rgb(0_0_0/0.12)]',
  );
  const style: CSSProperties = {
    ...(enableColumnResizing ? { width: header.getSize() } : {}),
    ...(pinnedCellStyle(header.column, 'header') ?? {}),
  };
  return {
    className: className || undefined,
    style: Object.keys(style).length > 0 ? style : undefined,
  };
}

function ColumnHeader<T>({
  header,
  enableSorting,
  enableFiltering,
  enableColumnResizing,
  testId,
}: ColumnHeaderProps<T>) {
  const canResize = enableColumnResizing && header.column.getCanResize();
  const { className, style } = headerCellChrome(header, enableColumnResizing);

  return (
    <TableHead
      data-column-id={header.column.id}
      className={cn(canResize && 'relative', className)}
      style={style}
      data-testid={`${testId}-header-${header.column.id}`}
    >
      <ColumnHeaderInner
        header={header}
        enableSorting={enableSorting}
        enableFiltering={enableFiltering}
        testId={testId}
      />
      {canResize && <ResizeHandle header={header} testId={testId} />}
    </TableHead>
  );
}

/** A drag-reorderable header cell: a grip handle carries the dnd listeners (kept
 *  separate from the sort button so a click still sorts), and the cell lifts
 *  while dragging. */
function SortableColumnHeader<T>({
  header,
  enableSorting,
  enableFiltering,
  enableColumnResizing,
  testId,
}: ColumnHeaderProps<T>) {
  const canResize = enableColumnResizing && header.column.getCanResize();
  const { className, style } = headerCellChrome(header, enableColumnResizing);
  const { setNodeRef, transform, transition, attributes, listeners, isDragging } = useSortable({
    id: header.column.id,
    // Neighbours glide aside rather than snap; the eased curve settles without overshoot.
    transition: { duration: 220, easing: 'cubic-bezier(0.25, 1, 0.5, 1)' },
  });

  return (
    <TableHead
      ref={setNodeRef}
      data-column-id={header.column.id}
      className={cn('group/th relative', isDragging && 'z-20 shadow-md', className)}
      data-testid={`${testId}-header-${header.column.id}`}
      style={{
        ...style,
        // Horizontal only: a column slides along the header row, never down into the rows.
        transform: CSS.Translate.toString(transform ? { ...transform, y: 0 } : null),
        transition,
      }}
    >
      {/* Drag handle centred along the cell's top edge — out of the label's way, so the
          label and sort icon keep the full width. Faint at rest, clear on header hover. */}
      <button
        type="button"
        aria-label={`Drag to reorder ${header.column.id} column`}
        title="Drag to reorder"
        className="text-muted-foreground hover:text-foreground hover:bg-muted focus-visible:ring-ring absolute top-0 left-1/2 -translate-x-1/2 cursor-grab touch-none rounded-b-sm px-1.5 leading-none opacity-40 transition group-hover/th:opacity-100 focus-visible:opacity-100 focus-visible:ring-2 focus-visible:outline-none active:cursor-grabbing"
        data-testid={`${testId}-reorder-${header.column.id}`}
        {...attributes}
        {...listeners}
      >
        <GripHorizontal className="h-3.5 w-3.5" strokeWidth={2.5} aria-hidden="true" />
      </button>
      <ColumnHeaderInner
        header={header}
        enableSorting={enableSorting}
        enableFiltering={enableFiltering}
        testId={testId}
      />
      {canResize && <ResizeHandle header={header} testId={testId} />}
    </TableHead>
  );
}

/**
 * The draggable divider on a column's trailing edge. Pointer-draggable via
 * TanStack's own handler, and arrow-keyable so keyboard users can resize too.
 */
function ResizeHandle<T>({
  header,
  testId,
}: {
  header: Header<T, unknown>;
  /** Automation prefix inherited from the parent DataTable. */
  testId: string;
}) {
  const table = header.getContext().table;
  const columnId = header.column.id;
  const beforeRightPinned =
    table.getRightLeafColumns().length > 0 && header.column.getIsLastColumn('center');

  function nudge(delta: number): void {
    const next = Math.max(header.column.columnDef.minSize ?? 60, header.getSize() + delta);
    table.setColumnSizing((prev) => ({ ...prev, [columnId]: next }));
  }

  return (
    <button
      type="button"
      aria-label={`Resize ${columnId} column. Use the left and right arrow keys to adjust, Home to reset.`}
      data-testid={`${testId}-resize-${columnId}`}
      className={cn(
        'absolute top-0 right-0 z-10 flex h-full w-3 cursor-col-resize touch-none items-stretch justify-end select-none',
        'group focus-visible:ring-ring focus-visible:ring-2 focus-visible:outline-none',
      )}
      onMouseDown={header.getResizeHandler()}
      onTouchStart={header.getResizeHandler()}
      // Stops a divider drag from also sorting the column it sits on.
      onClick={(e) => {
        e.stopPropagation();
      }}
      onKeyDown={(e) => {
        if (e.key === 'ArrowLeft') {
          e.preventDefault();
          nudge(-KEYBOARD_RESIZE_STEP);
        } else if (e.key === 'ArrowRight') {
          e.preventDefault();
          nudge(KEYBOARD_RESIZE_STEP);
        } else if (e.key === 'Home') {
          e.preventDefault();
          table.setColumnSizing((prev) =>
            Object.fromEntries(Object.entries(prev).filter(([id]) => id !== columnId)),
          );
        }
      }}
    >
      <span
        aria-hidden="true"
        className={cn(
          'h-full w-px transition-colors',
          header.column.getIsResizing()
            ? 'bg-primary w-0.5'
            : // Just before a right-pinned column its own divider already marks this edge —
              // a second line here doubled it.
              beforeRightPinned
              ? 'group-hover:bg-primary/50 bg-transparent'
              : 'bg-border group-hover:bg-primary/50',
        )}
      />
    </button>
  );
}

function SortIcon({ sorted }: { sorted: false | 'asc' | 'desc' }) {
  if (sorted === 'asc') {
    return <ArrowUp className="text-primary h-3.5 w-3.5 shrink-0" aria-hidden="true" />;
  }
  if (sorted === 'desc') {
    return <ArrowDown className="text-primary h-3.5 w-3.5 shrink-0" aria-hidden="true" />;
  }
  // Unsorted: a faint two-way arrow that firms up when the header is hovered.
  return (
    <ArrowUpDown
      className="text-muted-foreground/50 group-hover/sort:text-muted-foreground h-3.5 w-3.5 shrink-0 transition-colors"
      aria-hidden="true"
    />
  );
}

/**
 * The drag styling for the column being reordered, scoped to this table (`:has()` on the
 * header's `data-dnd-scope`): the whole column is tinted and follows the pointer, and the column
 * it would land on shows a primary insertion edge. Column ids are CSS-escaped.
 */
function dragColumnStyle(
  scope: string,
  drag: { active: string; over: string | null; dx: number },
): string {
  const table = `table:has(> thead[data-dnd-scope="${cssEscape(scope)}"])`;
  const active = `[data-column-id="${cssEscape(drag.active)}"]`;
  const rules = [
    `${table} td${active}{background-color:color-mix(in oklab,var(--primary) 7%,var(--background));transform:translateX(${String(drag.dx)}px);position:relative;z-index:5;box-shadow:0 0 0 1px color-mix(in oklab,var(--primary) 25%,transparent);}`,
    `${table} th${active}{background-color:color-mix(in oklab,var(--primary) 12%,var(--neutral-30));}`,
  ];
  if (drag.over && drag.over !== drag.active) {
    const over = `[data-column-id="${cssEscape(drag.over)}"]`;
    const edge = drag.dx > 0 ? 'inset -2px 0 0 var(--primary)' : 'inset 2px 0 0 var(--primary)';
    rules.push(`${table} :is(td,th)${over}{box-shadow:${edge};}`);
  }
  return rules.join('');
}

/** `CSS.escape` (the DOM one) — `CSS` here is dnd-kit's transform helper. */
function cssEscape(value: string): string {
  return globalThis.CSS.escape(value);
}

/** Radix Select forbids an empty-string item value, so "no filter" needs a sentinel. */
const ALL_VALUES = '__all__';
/** Radix Select forbids an empty item value: a blank option travels as this, mapped back to ''. */
const BLANK_VALUE = '__blank__';

/**
 * A header filter over a known set of values (`filterOptions`): "All" plus each
 * value with its count. Picking "All" clears the filter.
 */
function HeaderFilterSelect({
  columnId,
  options,
  value,
  onChange,
  testId,
}: {
  columnId: string;
  options: DataTableFilterOption[];
  value: string;
  onChange: (next: string) => void;
  /** Table test id prefix; the trigger gets `${testId}-filter-${columnId}`. */
  testId: string;
}) {
  return (
    <Select
      value={value === '' ? ALL_VALUES : value}
      onValueChange={(next) => {
        // The blank option's sentinel maps back to the real empty value it stands for.
        onChange(next === ALL_VALUES || next === BLANK_VALUE ? '' : next);
      }}
    >
      <SelectTrigger
        className="h-7 cursor-pointer text-xs font-normal"
        aria-label={`Filter ${columnId}`}
        data-testid={`${testId}-filter-${columnId}`}
      >
        <SelectValue />
      </SelectTrigger>
      <SelectContent className="max-h-72" data-testid={`${testId}-filter-${columnId}-content`}>
        <SelectItem
          value={ALL_VALUES}
          className="cursor-pointer text-xs"
          data-testid={`${testId}-filter-${columnId}-option-all`}
        >
          All
        </SelectItem>
        {options.map((option) => (
          <SelectItem
            key={option.value}
            value={option.value || BLANK_VALUE}
            className="cursor-pointer text-xs"
            data-testid={`${testId}-filter-${columnId}-option-${option.value || 'blank'}`}
          >
            <span className="flex items-center gap-2">
              <span className="truncate">{option.label || '(blank)'}</span>
              {option.count !== undefined && (
                <span className="text-muted-foreground tabular-nums">
                  {option.count.toLocaleString()}
                </span>
              )}
            </span>
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
